import {HttpInterceptorFn} from "@angular/common/http";
import {inject} from "@angular/core";
import {AuthService} from "../services/auth.service";
import {LoadingService} from "../services/loading.service";
import {SILENT_REQUEST} from "../http/silent-request";
import {finalize} from "rxjs/operators";

// URLs that ONLY accept the pending_mfa JWT (issued by /auth/google
// on the challenge/enroll branches). Any other endpoint MUST reject
// this token as INVALID_TOKEN_TYPE (see @gozem-api/security#
// verifyPendingMfa) — matching on the URL suffix here keeps the
// interceptor synchronous without an extra token-shape check.
const PENDING_MFA_URLS = ["/mfa/enroll", "/mfa/enroll/verify", "/mfa/challenge"];

function isPendingMfaCall(url: string): boolean {
    return PENDING_MFA_URLS.some((suffix) => url.endsWith(suffix));
}

/**
 * Adds the admin JWT (when present) as a Bearer token on every outbound
 * request, and bumps the global in-flight counter so the layout can
 * show progress.
 *
 * For the MFA challenge/enrol flow the pending_mfa JWT is used
 * instead — that token is what the server accepts on those endpoints
 * (see auth.service.ts#_pendingMfa). Everywhere else the regular
 * admin access token is used.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const auth = inject(AuthService);
    const loading = inject(LoadingService);

    const token = isPendingMfaCall(req.url) ? auth.pendingMfaToken() : auth.token();
    const authedReq = token ? req.clone({setHeaders: {Authorization: `Bearer ${token}`}}) : req;

    // Background polls opt out of the top-bar indicator via
    // `silentContext()` (see core/http/silent-request.ts). They still
    // go through the full interceptor chain — only the visible loading
    // counter is bypassed. User-initiated calls (form submits, page
    // loads, click-driven refreshes) omit the context and continue to
    // drive the indicator normally.
    // Static translation assets are loaded by @ngx-translate's
    // TranslateHttpLoader, which internally wraps its `HttpClient.get(
    // "/i18n/xx.json")` in a `shareReplay(1)`-style cache. That keeps
    // the SOURCE observable subscribed for the lifetime of the app so
    // subsequent language switches / lazy modules can re-read the
    // cached JSON without another network round-trip. Consequence:
    // `finalize()` on the outer observable NEVER fires (there's always
    // at least one downstream subscriber — the cache holder), so the
    // pending counter never decrements. The request itself completed
    // instantly with a 200 from the dev server; what leaks is the
    // subscription, not the network call. Same reasoning for any
    // future `/assets/**/*.json` static loaders — treat them all as
    // uncounted background reads.
    const isStaticAsset = req.url.startsWith("/i18n/") || req.url.startsWith("/assets/");

    const silent = req.context.get(SILENT_REQUEST) || isStaticAsset;
    if (silent) {
        return next(authedReq);
    }

    const id = loading.start(`${req.method} ${req.urlWithParams}`);
    return next(authedReq).pipe(finalize(() => loading.end(id)));
};
